import ReactGA from 'react-ga4';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

// Initialize GA4 (call this in App.tsx)
export const initializeAnalytics = () => {
  const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  
  if (measurementId && process.env.NODE_ENV === 'production') {
    ReactGA.initialize(measurementId, {
      testMode: process.env.NODE_ENV !== 'production'
    });
  }
};

// Track PDF download event
export const trackPDFDownload = (data: {
  moduleId: number;
  filename: string;
  userId?: string;
  userTier?: string;
}) => {
  if (process.env.NODE_ENV === 'production') {
    // GA4 event tracking - react-ga4 accepts standard event parameters
    ReactGA.event({
      category: 'Downloads',
      action: 'PDF Download',
      label: `Module ${data.moduleId} - ${data.filename}`,
      value: data.moduleId
    } as any); // Type assertion to allow custom parameters if needed
    
    // Send custom parameters separately using gtag if needed
    if (window.gtag) {
      window.gtag('event', 'PDF Download', {
        module_id: data.moduleId.toString(),
        filename: data.filename,
        ...(data.userId && { user_id: data.userId }),
        ...(data.userTier && { user_tier: data.userTier })
      });
    }
  }
  
  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 PDF Download Tracked:', data);
  }
};

// Track PDF teaser view
export const trackPDFTeaserView = (data: {
  moduleId: number;
  userId?: string;
}) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category: 'Engagement',
      action: 'PDF Teaser Viewed',
      label: `Module ${data.moduleId}`,
      value: data.moduleId
    });
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 PDF Teaser Viewed:', data);
  }
};

// Track upsell click
export const trackUpsellClick = (data: {
  moduleId: number;
  upsellType: 'advanced' | 'masterclass' | 'consulting';
  userId?: string;
}) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category: 'Upsell',
      action: 'Upsell Clicked',
      label: `${data.upsellType} - Module ${data.moduleId}`,
      value: data.moduleId
    });
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Upsell Clicked:', data);
  }
};

// Track badge earned
export const trackBadgeEarned = (data: {
  moduleId: number;
  badgeName: string;
  userId?: string;
}) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category: 'Gamification',
      action: 'Badge Earned',
      label: `${data.badgeName} - Module ${data.moduleId}`,
      value: data.moduleId
    });
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Badge Earned:', data);
  }
};

// Track user action (generic)
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (process.env.NODE_ENV === 'production') {
    ReactGA.event({
      category,
      action,
      label,
      value
    });
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Event Tracked:', { category, action, label, value });
  }
};

/**
 * Track form_start - GA4 recommended event when user begins filling a form.
 * Use on first input focus. Fires via gtag (always loaded) for reliability.
 */
export const trackFormStart = (formName: string, formId?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'form_start', {
      form_name: formName,
      ...(formId && { form_id: formId })
    });
  }
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Form Start:', formName);
  }
};

/**
 * Track generate_lead - GA4 key event for successful lead/sign-up.
 * Use when form submit succeeds (free course, waitlist, request access).
 */
export const trackGenerateLead = (formName: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'generate_lead', {
      form_name: formName,
      ...(value !== undefined && { value })
    });
  }
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Generate Lead:', formName);
  }
};

